import React, { useMemo } from 'react';
import { MapContainer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ClusterShop } from '../hooks/useClusterShops';
import { Cluster } from '../hooks/useClusters';
import ClusterBoundaryLayer from './ClusterBoundaryLayer';
import { createMapPinIcon } from '../../coffee_shops/components/MapPin';
import { RatingBadge } from '../../coffee_shops/components';
import { CoffeeMapBasemap, COFFEE_MAP_CONTAINER_CLASS } from '../../coffee_shops/components/CoffeeMapBasemap';

const FitBounds: React.FC<{ shops: ClusterShop[]; cluster?: Cluster | null }> = ({ shops, cluster }) => {
    const map = useMap();

    React.useEffect(() => {
        if (shops.length > 0) {
            const bounds = L.latLngBounds(
                shops.map(s => [s.coordinates.lat, s.coordinates.lng] as [number, number])
            );
            map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14 });
        } else if (cluster?.center) {
            map.setView([cluster.center!.lat, cluster.center!.lng], 12);
        }
    }, [shops, cluster, map]);

    return null;
};

interface ClusterShopsMapProps {
    clusterId: string | null;
    cluster: Cluster | null;
    shops: ClusterShop[];
    loading: boolean;
    onEditShop?: (shop: ClusterShop) => void;
}

const DEFAULT_CENTER: [number, number] = [40.6401, 22.9444];

const ClusterShopsMap: React.FC<ClusterShopsMapProps> = ({
    cluster,
    shops,
    loading,
    onEditShop
}) => {
    const center = useMemo(() => {
        if (shops.length > 0) {
            const avgLat = shops.reduce((s, sh) => s + sh.coordinates.lat, 0) / shops.length;
            const avgLng = shops.reduce((s, sh) => s + sh.coordinates.lng, 0) / shops.length;
            return [avgLat, avgLng] as [number, number];
        }
        if (cluster?.center) {
            return [cluster.center!.lat, cluster.center!.lng] as [number, number];
        }
        return DEFAULT_CENTER;
    }, [shops, cluster]);

    const boundaryClusters = useMemo(() => {
        if (!cluster?.boundary) return [];
        return [cluster];
    }, [cluster]);

    if (loading) {
        return (
            <div className="rounded-xl border border-ink/15 overflow-hidden bg-muted/20 flex items-center justify-center" style={{ height: '400px' }}>
                <div className="text-muted-foreground">Loading map...</div>
            </div>
        );
    }

    return (
        <div className="rounded-xl border border-ink/15 overflow-hidden" style={{ height: '400px' }}>
            <MapContainer
                center={center}
                zoom={12}
                className={COFFEE_MAP_CONTAINER_CLASS}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom
            >
                <CoffeeMapBasemap />
                <FitBounds shops={shops} cluster={cluster} />
                {boundaryClusters.length > 0 && (
                    <ClusterBoundaryLayer clusters={boundaryClusters} />
                )}
                {shops.map((shop) => {
                    const icon = L.icon({
                        iconUrl: createMapPinIcon(shop.averageRating || 0, false),
                        iconSize: [28, 28],
                        iconAnchor: [14, 14],
                        popupAnchor: [0, -14]
                    });
                    return (
                        <Marker
                            key={shop._id}
                            position={[shop.coordinates.lat, shop.coordinates.lng]}
                            icon={icon}
                        >
                            <Popup>
                                <div className="min-w-[180px] p-1">
                                    <h3 className="font-bold text-sm text-ink mb-1">{shop.name}</h3>
                                    {shop.address && (
                                        <p className="text-xs text-muted-foreground mb-1">{shop.address}</p>
                                    )}
                                    <div className="flex items-center gap-2 mb-2">
                                        <RatingBadge rating={shop.averageRating} size="sm" showMax={false} />
                                        <span className="text-xs text-muted-foreground">
                                            {shop.totalRatings} ratings · {shop.shopType.replace('-', ' ')}
                                        </span>
                                    </div>
                                    {onEditShop && shop.status !== 'archived' && (
                                        <button
                                            type="button"
                                            onClick={() => onEditShop(shop)}
                                            className="w-full px-3 py-1.5 bg-ink text-paper text-xs font-semibold rounded-lg hover:bg-ink/80"
                                        >
                                            Edit
                                        </button>
                                    )}
                                </div>
                            </Popup>
                        </Marker>
                    );
                })}
            </MapContainer>
        </div>
    );
};

export default ClusterShopsMap;
